import { createPortal } from "react-dom";

/**
 * Multi-step progress modal used to surface a wallet flow (approve, send,
 * confirm) in a styled container.
 *
 * Each entry of the `steps` prop has the shape:
 *   { key, label, doneLabel, status }
 *   where status is one of: "pending" | "loading" | "done" | "error"
 *
 * The modal does not render dismiss controls - the caller decides when it
 * closes by toggling the `open` prop, since wallet flows must not be
 * interrupted mid-transaction.
 *
 * Portaled into `document.body` so the backdrop's blur covers the
 * sticky page header (which would otherwise sit above an in-tree z-50).
 */

function StepIcon({ status }) {
    if (status === "loading") {
        return (
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-[#bfb67b]/40 border-t-[#FBC206]" />
            </span>
        );
    }
    if (status === "done") {
        return (
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FBC206]">
                <svg
                    className="h-3.5 w-3.5 text-[#10281b]"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polyline points="4 11 8 15 16 6" />
                </svg>
            </span>
        );
    }
    if (status === "error") {
        return (
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FDE2E2] ring-1 ring-[#C93030]/40">
                <svg
                    className="h-3.5 w-3.5 text-[#C93030]"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <line x1="5" y1="5" x2="15" y2="15" />
                    <line x1="15" y1="5" x2="5" y2="15" />
                </svg>
            </span>
        );
    }
    return (
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-[#bfb67b]/40">
            <span className="h-2 w-2 rounded-full bg-[#e6c985]/50" />
        </span>
    );
}

function statusTextClass(status) {
    if (status === "done") return "text-white font-medium";
    if (status === "error") return "text-[#C93030] font-semibold";
    if (status === "loading") return "text-white font-semibold";
    return "text-[#e6c985]/60";
}

export default function FlowModal({ open, title = "Flow Steps", subtitle, steps = [] }) {
    if (!open) return null;
    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0E2C19]/55 backdrop-blur-sm p-4"
            role="presentation"
        >
            <div
                className="w-full max-w-md rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] shadow-xl p-5"
                role="dialog"
                aria-modal="true"
                aria-labelledby="flow-modal-title"
            >
                <div className="flex items-center justify-between">
                    <h3
                        id="flow-modal-title"
                        className="font-manrope text-[15px] font-bold text-[#e6c985]"
                    >
                        {title}
                    </h3>
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#FBC206] px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-[#10281b]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#10281b] animate-pulse" />
                        In Progress
                    </span>
                </div>
                {subtitle ? (
                    <p className="mt-1 text-[11px] text-[#e6c985]/70">{subtitle}</p>
                ) : null}

                <ol className="mt-5 space-y-3 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b]/40 p-3">
                    {steps.map((step) => (
                        <li
                            key={step.key}
                            className="flex items-center gap-3 rounded-[8px] bg-white/5 ring-1 ring-[#bfb67b]/30 px-3 py-2"
                        >
                            <StepIcon status={step.status} />
                            <span className={`text-[12px] ${statusTextClass(step.status)}`}>
                                {step.status === "done" && step.doneLabel ? step.doneLabel : step.label}
                            </span>
                        </li>
                    ))}
                </ol>

                <p className="mt-4 text-[10.5px] leading-snug text-[#e6c985]/70">
                    Please confirm any wallet prompts. Do not close this window until both steps complete.
                </p>
            </div>
        </div>,
        document.body
    );
}
